import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import {
  BackButton,
  CardQuotation,
  Error,
  InternalDepositReport,
  InternalDepositTable,
  Loading,
  ManufacturingOrderTable,
  QuotationProductTable,
  QuotationReport,
  UpdateRatingDialog,
  ViewTitle,
} from '@iarcpsu/emanufac-components/src/components';
import { Button } from '@mui/material';
import PropTypes from 'prop-types';

import * as actions from '@/redux/actions';

/**
 * @function DetailQuotation
 * @description Display a collections or a list of EditProcessTemplateTableView from database
 */

export default function DetailQuotation({ title, subtitle, log = false }) {
  const dispatch = useDispatch();
  const quotation = log
    ? useSelector((state) => state.quotationLog)
    : useSelector((state) => state.quotation);
  const information = useSelector((state) => state.information);
  const manufacturingOrder = useSelector((state) => state.manufacturingOrder);
  const internalDeposit = useSelector((state) => state.internalDeposit);
  const me = useSelector((state) => state.me);
  const params = useParams();
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);

  const queryDataFromServer = async () => {
    try {
      if (log) {
        dispatch(actions.quotationLogGet(params.id));
      } else {
        dispatch(actions.quotationGet(params.id));
      }
    } catch (error) {
      dispatch(actions.quotationError());
    }
  };

  const queryMOandID = async () => {
    try {
      dispatch(
        actions.manufacturingOrderAll({
          quotation: params.id,
          size,
          page,
        }),
      );
      dispatch(
        actions.internalDepositAll({ quotation: params.id, size, page }),
      );
    } catch (error) {
      console.error('Query MO and ID Failed', error);
    }
  };

  useEffect(() => {
    queryDataFromServer();
    return () => {};
  }, [params]);

  useEffect(() => {
    queryMOandID();

    return () => {};
  }, [size, page]);

  const handleUpdateCustomerRating = async (customerId, newRating) => {
    try {
      await dispatch(
        actions.customerPut(customerId, {
          rating: newRating,
          employee: me?.userData?._id,
        }),
      );
      queryDataFromServer();
    } catch (error) {
      console.error('Edit Customer Fail', error);
      window.alert('ไม่สามารถแก้ไขคะแนนลูกค้าได้', error?.message);
    }
  };

  const handlePrintIDPDF = (row) => {
    InternalDepositReport(row, information);
  };

  const renderTitle = () => <ViewTitle title={title} subtitle={subtitle} />;

  const handlePrintPdf = () => {
    try {
      QuotationReport(quotation, information, true);
    } catch (error) {
      QuotationReport(quotation, information);
    }
  };

  if (quotation.isLoading) {
    return <Loading />;
  }

  if (!quotation.isLoading && quotation.isCompleted) {
    return (
      <div>
        <UpdateRatingDialog
          customer={quotation?.customer}
          handleUpdate={(customerId, newRating) => {
            // setRatingDialogOpen(false);
            handleUpdateCustomerRating(customerId, newRating);
          }}
          open={ratingDialogOpen}
          handleCloseDialog={() => setRatingDialogOpen(false)}
        />
        <div className="flex items-center justify-between">
          {renderTitle()}
          <div className="flex items-center gap-1">
            <Button
              variant="contained"
              color={'teal'}
              onClick={() => {
                handlePrintPdf();
              }}
            >
              พิมพ์
            </Button>
            {!log && (
              <Link
                to={
                  quotation?.completed || quotation?.deleted
                    ? ''
                    : `/spm/quotation/edit/${quotation?._id}`
                }
              >
                <Button
                  variant="contained"
                  color={'warning'}
                  disabled={quotation?.completed || quotation?.deleted}
                >
                  แก้ไข
                </Button>
              </Link>
            )}
            {!quotation?.completed && !quotation?.deleted && (
              <div className="flex flex-row gap-1 ">
                <div>
                  <Link
                    to={`/mms/manufacturing-orders/create?quotation=${quotation?._id}`}
                  >
                    <Button variant="contained" color="success">
                      สั่งผลิต
                    </Button>
                  </Link>
                </div>
                <div>
                  <Link to={`/spm/quotation/reject/${quotation?._id}`}>
                    <Button variant="contained" color="error">
                      ปฏิเสธ
                    </Button>
                  </Link>
                </div>
                <div>
                  <Link to={`/spm/quotation/revise/${quotation?._id}`}>
                    <Button variant="contained" color="secondary">
                      Revise
                    </Button>
                  </Link>
                </div>{' '}
                <div>
                  <Link
                    to={`/spm/internal-deposit/create?quotation=${quotation?._id}`}
                  >
                    <Button variant="contained" color="magenta">
                      เบิกภายใน
                    </Button>{' '}
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
        <BackButton />
        <div className="my-2">
          <CardQuotation
            quotation={quotation}
            setRatingDialogOpen={setRatingDialogOpen}
          />
        </div>
        <div className="my-2">
          <QuotationProductTable quotation={quotation} />
        </div>
        <div className="my-4 font-semibold">คำสั่งผลิต</div>
        <div className="my-2">
          <ManufacturingOrderTable
            manufacturingOrder={manufacturingOrder}
            page={page}
            size={size}
            handleChangePage={(event, newPage) => setPage(newPage + 1)}
            handleChangeRowsPerPage={(event) => {
              setSize(parseInt(event.target.value, 10));
              setPage(1);
            }}
            displayCancleButton={false}
            showInsideStatus={false}
            showProduct={false}
          />
        </div>{' '}
        <div className="my-4 font-semibold">ใบเบิกใช้ภายใน</div>
        <div className="my-2">
          <InternalDepositTable
            internalDeposit={internalDeposit}
            information={information}
            page={page}
            setPage={setPage}
            size={size}
            setSize={setSize}
            handlePrintPdf={handlePrintIDPDF}
          />
        </div>{' '}
      </div>
    );
  }
  return <Error />;
}

DetailQuotation.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  log: PropTypes.bool,
};

DetailQuotation.defaultProps = {
  title: '',
  subtitle: '',
};
